import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchoolId } from '../utils/activeSchool';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { isCoachAccount } from '../utils/roles';

const API = getApiBaseUrl();

const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
    fontFamily: 'inherit'
};

export default function GroupsPage() {
    const { t } = useTranslation(['groups', 'students', 'common', 'errors']);
    const coachView = isCoachAccount();
    const [schoolId, setSchoolId] = useState(null);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editGroupId, setEditGroupId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({ name: '', scheduleJSON: '' });

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => { loadData(); }, []);

    async function loadData() {
        setLoading(true);
        try {
            const schoolRes = await getSchoolForSession(api);
            const sid = getSchoolIdFromResponse(schoolRes) || getActiveSchoolId();
            setSchoolId(sid);
            const res = await api.get(`/school/${sid}/groups`);
            setGroups(res.data);
        } catch (e) {
            setError(e.response?.data?.message || t('errors:loadFailed'));
        }
        setLoading(false);
    }

    function openCreate() {
        setForm({ name: '', scheduleJSON: '' });
        setEditGroupId(null);
        setShowForm(true);
    }

    function openEdit(g) {
        setForm({ name: g.name, scheduleJSON: g.scheduleJSON || '' });
        setEditGroupId(g.id);
        setShowForm(true);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            if (editGroupId) {
                await api.put(`/school/${schoolId}/groups/${editGroupId}`, {
                    name: form.name,
                    scheduleJSON: form.scheduleJSON || null
                });
            } else {
                await api.post(`/school/${schoolId}/groups`, {
                    name: form.name,
                    scheduleJSON: form.scheduleJSON || null
                });
            }
            setShowForm(false);
            setEditGroupId(null);
            setForm({ name: '', scheduleJSON: '' });
            await loadData();
        } catch (err) {
            setError(err.response?.data?.message || t('groups:saveFailed'));
        }
        setSaving(false);
    }

    const filtered = groups.filter(g =>
        g.name.toLowerCase().includes(search.toLowerCase())
    );

    const pageActions = !coachView ? (
        <button
            type="button"
            className={showForm ? 'sp-btn sp-btn--outline' : 'sp-btn sp-btn--primary'}
            onClick={() => showForm ? setShowForm(false) : openCreate()}
        >
            {showForm ? t('common:cancel') : t('groups:createButton')}
        </button>
    ) : null;

    if (loading) {
        return (
            <OwnerPageLayout title={t('groups:title')} maxWidth={860}>
                <div className="sp-loading-page">{t('common:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={`🏷️ ${t('groups:title')}`}
            subtitle={t('groups:subtitle', { count: groups.length })}
            actions={pageActions}
            maxWidth={860}
        >
            {error && (
                <div style={{
                    background: '#fce4ec', color: '#c62828', padding: '12px 16px',
                    borderRadius: '10px', marginBottom: '16px', fontSize: '14px'
                }}>
                    {error}
                </div>
            )}

            {!coachView && showForm && (
                <div className="sp-panel" style={{ borderColor: 'var(--sp-primary)', marginBottom: 20 }}>
                    <h3 className="sp-panel__title" style={{ marginBottom: 20 }}>
                        {editGroupId ? t('groups:editTitle') : t('groups:newGroupTitle')}
                    </h3>
                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                    {t('groups:name')}
                                </label>
                                <input
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder={t('groups:namePlaceholder')}
                                    required style={inputStyle}
                                    onFocus={e => e.target.style.borderColor = '#EB3333'}
                                    onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                    {t('groups:scheduleLabel')}
                                </label>
                                <input
                                    value={form.scheduleJSON}
                                    onChange={e => setForm({ ...form, scheduleJSON: e.target.value })}
                                    placeholder={t('groups:schedulePlaceholder')}
                                    style={inputStyle}
                                    onFocus={e => e.target.style.borderColor = '#EB3333'}
                                    onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                />
                            </div>
                        </div>
                        <button type="submit" disabled={saving} style={{
                            padding: '11px 28px',
                            background: saving ? '#94a3b8' : '#EB3333',
                            color: 'white', border: 'none', borderRadius: '10px',
                            fontSize: '14px', fontWeight: '700', cursor: 'pointer'
                        }}>
                            {saving ? t('groups:saving') : editGroupId ? t('groups:saveChanges') : t('groups:createButton')}
                        </button>
                    </form>
                </div>
            )}

            {groups.length > 5 && (
                <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <span style={{
                        position: 'absolute', left: '14px', top: '50%',
                        transform: 'translateY(-50%)', fontSize: '16px'
                    }}>🔍</span>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('groups:searchPlaceholder')}
                        style={{ ...inputStyle, paddingLeft: '40px', background: 'white' }}
                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                    />
                </div>
            )}

            {filtered.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '48px 20px',
                    color: '#aaa', fontSize: '15px',
                    background: 'white', borderRadius: '14px', border: '1px solid var(--sp-gray-100)'
                }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏷️</div>
                    {search ? t('groups:notFound') : t('groups:emptyState')}
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                    {filtered.map(g => (
                        <div key={g.id} style={{
                            background: 'white', borderRadius: '14px', padding: '16px 20px',
                            border: '1px solid var(--sp-gray-100)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: '22px' }}>🏷️</span>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: '700', fontSize: '15px' }}>{g.name}</div>
                                    {g.scheduleJSON && (
                                        <div style={{ fontSize: '12px', color: '#888' }}>📅 {g.scheduleJSON}</div>
                                    )}
                                </div>
                                <span style={{
                                    background: '#e8f5e9', color: '#2e7d32',
                                    padding: '3px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {t('groups:studentsCount', { count: g.studentCount })}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <a href="/students" style={{
                                    padding: '8px 14px', borderRadius: '8px',
                                    background: 'white', border: '1px solid var(--sp-gray-200)',
                                    fontSize: '13px', color: '#555', textDecoration: 'none'
                                }}>
                                    {t('groups:viewStudents')}
                                </a>
                                {!coachView && (
                                    <button
                                        onClick={() => openEdit(g)}
                                        style={{
                                            padding: '8px 14px', borderRadius: '8px',
                                            background: 'white', border: '1px solid var(--sp-gray-200)',
                                            cursor: 'pointer', fontSize: '13px', color: '#555'
                                        }}
                                    >
                                        {t('groups:edit')}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </OwnerPageLayout>
    );
}
